import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Form } from 'react-bootstrap';
import SimpleCheck from 'reusable/Check';
import SimpleSelect from 'reusable/Select';
import SimpleText from 'reusable/Text';
import * as FaIcons from 'react-icons/fa';
import ReactJson from 'react-json-view';
import styled from 'styled-components';
import { GRAPHITE_COLOR, MAIN_BLUE_COLOR } from 'containers/App/constants';

const StyledModal = styled(Modal)`
  background-color: #0005;

  .modal-content {
    border: 0;
    border-radius: 8px;
    box-shadow: 0 18px 48px rgba(65, 70, 80, 0.18);
    overflow: hidden;
  }

  .modal-header {
    background: #f3f5f8;
    border-bottom: 1px solid #d9dde3;
  }

  .modal-title {
    color: ${GRAPHITE_COLOR};
    font-size: 1.05rem;
    font-weight: 700;
  }

  .modal-body {
    max-height: min(72vh, 760px);
    overflow: auto;
  }

  .form-group {
    margin-bottom: 12px;
  }

  .form-control:focus {
    border-color: ${MAIN_BLUE_COLOR};
    box-shadow: 0 0 0 0.18rem rgba(22, 81, 119, 0.16);
  }

`;

function CommandEditModal({
  show,
  onHide,
  action,
  section,
  index,
  value,
  onSubmit,
}) {
  const [editValue, setEditValue] = useState(value);
  const [type, setType] = useState('object');

  useEffect(() => {
    setType(typeof value);
    setEditValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, index]);

  useEffect(() => {
    if (typeof editValue !== type) {
      setEditValue(type === 'string' ? '' : {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const errorMessages = () => {
    if (type === 'string') return '';
    if (!editValue.display && !editValue.icon)
      return 'Display or Icon is Required';
    if (editValue.display && editValue.icon)
      return 'Only one of Display or Icon is allowed';
    if (!editValue.command) return 'Command is required';
    return '';
  };

  const updateKeyBinding = ({ updated_src }) =>
    setEditValue(prev => ({
      ...prev,
      keyBinding: Object.keys(updated_src).length ? updated_src : undefined,
    }));

  const updateExtras = ({ updated_src }) =>
    setEditValue(prev => ({
      ...prev,
      extras: Object.keys(updated_src).length ? updated_src : undefined,
    }));

  return (
    <StyledModal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {action === 'edit' ? 'Edit Command' : 'Add Command'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {section === 'Controls' && (
          <>
            <SimpleSelect
              label="type"
              value={type}
              options={['object', 'string']}
              onChange={setType}
              className="form-input"
            />
            <br />
          </>
        )}
        {type === 'string' ? (
          <SimpleText
            label="Placeholder"
            value={editValue || ''}
            onChange={setEditValue}
          />
        ) : (
          <>
            <SimpleText
              label="Display"
              value={editValue.display || ''}
              onChange={value =>
                setEditValue(prev => ({ ...prev, display: value }))
              }
            />
            <br />
            <SimpleSelect
              label="Icon"
              value={editValue.icon || ''}
              options={Object.keys(FaIcons)}
              onChange={value =>
                setEditValue(prev => ({ ...prev, icon: value }))
              }
              className="form-input"
              isClearable
            />
            <br />
            <SimpleText
              label="Tooltip"
              value={editValue.tooltip || ''}
              onChange={value =>
                setEditValue(prev => ({ ...prev, tooltip: value }))
              }
            />
            <br />
            <SimpleText
              label="Command *"
              value={editValue.command || ''}
              onChange={value =>
                setEditValue(prev => ({ ...prev, command: value }))
              }
            />
            <br />
            <SimpleText
              label="Dropdown Alias"
              value={editValue.dropdownAlias || ''}
              onChange={value =>
                setEditValue(prev => ({ ...prev, dropdownAlias: value }))
              }
            />
            <br />
            <SimpleCheck
              label="Visible"
              value={editValue.visible || false}
              onChange={value =>
                setEditValue(prev => ({ ...prev, visible: value }))
              }
            />
            <br />
            <Form.Group>
              <Form.Label>Key Binding</Form.Label>
              <ReactJson
                src={editValue.keyBinding || {}}
                onAdd={updateKeyBinding}
                onEdit={updateKeyBinding}
                onDelete={updateKeyBinding}
              />
            </Form.Group>
            <br />
            <Form.Group>
              <Form.Label>Extras</Form.Label>
              <ReactJson
                src={editValue.extras || {}}
                onAdd={updateExtras}
                onEdit={updateExtras}
                onDelete={updateExtras}
              />
            </Form.Group>
            <br />
            {errorMessages() && (
              <Alert variant="danger">{errorMessages()}</Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => {
            onSubmit(section, action, editValue, index);
            onHide();
          }}
          disabled={!!errorMessages()}
        >
          Save
        </Button>
        {action === 'add' ? null : (
          <Button
            variant="danger"
            onClick={() => {
              onSubmit(section, 'delete', editValue, index);
              onHide();
            }}
          >
            Delete
          </Button>
        )}
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
}

export default CommandEditModal;
